import { Box, Divider, Stack, Typography } from "@mui/material";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import AppLoader from "../../components/apploader";
import GlobalButton from "../../components/button";
import ReviewCartCard from "../../components/cards/ReviewCartCard";
import { useSnackbar } from "../../lib/context/snackbarContext";
import { setCartData } from "../../lib/redux/slices/cartSlice";
import { CURRENCY } from "../../lib/variables";
import { createEcomPaymentIntent } from "../../services/orderFn";
import { getAdminSetting } from "../../services/settingFn";
import { userPackageDetailByUrl } from "../../services/userPackageApiFn";
import { renderSubTotal } from "../../utils";
import ReactGA from "react-ga4";

const cardElementOptions = {
  style: {
    base: {
      color: "#000000",
      fontFamily: "'Roboto', sans-serif",
      fontSize: "15px",
      fontWeight: "500",
      letterSpacing: "0.025em",
      backgroundColor: "#f9f9f9",
      padding: "10px",
      border: "1px solid #ddd",
      borderRadius: "4px",
      "::placeholder": {
        color: "#888888",
      },
    },
    invalid: {
      color: "#ff5252",
      iconColor: "#ff5252",
    },
  },
  hidePostalCode: true,
};

const TotalRow = ({ keyyy, value, isLast }) => {
  return (
    <Stack
      direction={"row"}
      alignItems={"center"}
      justifyContent={"space-between"}
      margin={"8px 0 4px 0"}
    >
      <Typography
        variant={"body1"}
        fontSize={isLast ? "1.125rem" : "1rem"}
        fontWeight={isLast ? 500 : 400}
        color={"text.black"}
        textTransform={"capitalize"}
      >
        {keyyy}
      </Typography>
      <Typography
        variant={"body1"}
        fontSize={isLast ? "1.125rem" : "1rem"}
        fontWeight={isLast ? 500 : 400}
        color={"text.black"}
      >
        {value}
      </Typography>
    </Stack>
  );
};

const Billing = ({ isAcceptPolicy }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const { showSnackbar } = useSnackbar();
  const [shippingFee, setShippingFee] = useState(0);
  const [tax, setTax] = useState(0);
  const [platformFee, setPlatformFee] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [adminPercentage, setAdminPercentage] = useState(0);
  const cartData = useSelector((state) => state.cartReducer.cartData);
  const userData = useSelector((state) => state.userReducer.user);
  const userAddress = useSelector(
    (state) => state.userAddressReducer.userAddress
  );
  const { currentLanguage } = useSelector((state) => state.translationReducer);

  const [packageError, setPackageError] = useState(false);
  const [searchParams] = useSearchParams();
  const packageId = searchParams.get("package");
  const refferedBy = searchParams.get("via");
  const [packageDetail, setPackageDetail] = useState(null);

  // console.log(refferedBy,"JJHJHHJJHJHJHJH")

  useEffect(() => {
    fetchAdminSetting();
  }, []);

  useEffect(() => {
    if (packageId) {
      fetchPackageDetail(packageId, tax);
    }
  }, [packageId, tax]);

  const fetchPackageDetail = async (value, taxRate) => {
    try {
      const response = await userPackageDetailByUrl(value);
      if (response.data.status === "success") {
        const data = response.data.data;

        let newArr = data?.products?.map((itemmmmm) => {
          let taxAmount = (itemmmmm.finalPrice * taxRate) / 100;
          return { ...itemmmmm, taxAmount };
        });

        setPackageDetail({ ...data, products: newArr });
      } else {
        setPackageError(true);
      }
    } catch (error) {
      setPackageError(true);
    }
  };

  const fetchAdminSetting = async () => {
    try {
      setIsLoading(true);
      const response = await getAdminSetting();
      if (response.data.status === "success") {
        const data = response.data.data;

        setShippingFee(data.shippingFee);
        setTax(data.tax);
        setPlatformFee(data.platformFeeEcom);
        setAdminPercentage(data.ecomPercentage);

        let newArr = cartData.map((itemmmmm) => {
          let tax = (itemmmmm.finalPrice * data.tax) / 100;
          return { ...itemmmmm, taxAmount: tax };
        });

        dispatch(setCartData(newArr));
      } else {
        showSnackbar(response.data.message, "error");
      }
    } catch (error) {
      showSnackbar(error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  const selectedItems = packageDetail ? packageDetail.products : cartData;
  const shippingCost = renderSubTotal(selectedItems) >= 300 ? 0 : shippingFee;

  let platformCharges =
    (renderSubTotal(selectedItems) * Number(platformFee)) / 100;

  const renderPerCentageAmount = (params) => {
    const total = renderSubTotal(params) + shippingCost + platformCharges;
    return Math.ceil((total * tax) / 100);
  };

  const finalAmount =
    parseInt(renderSubTotal(selectedItems)) +
    parseInt(renderPerCentageAmount(selectedItems)) +
    parseInt(shippingCost);

  const handOrderAndCreatePayment = async (event) => {
    event.preventDefault();

    if (!packageDetail && cartData?.length === 0) {
      return showSnackbar(t("addOneItemInCart"), "error");
    }

    if (!userAddress) {
      return showSnackbar(t("shippingAddRequired"), "error");
    }

    if (!isAcceptPolicy) {
      return showSnackbar(t("policyValidation"), "error");
    }

    const order = packageDetail
      ? packageDetail.products.map((val) => ({
          ...val,
          orderStatus: "Pending",
          isReview: false,
          showDropDown: false,
          adminPercentage,
        }))
      : cartData.map((val) => ({
          ...val,
          orderStatus: "Pending",
          isReview: false,
          showDropDown: false,
          adminPercentage,
        }));

    if (!stripe || !elements) {
      return showSnackbar(t("stripeNotLoaded"), "error");
    }

    try {
      const cardElement = elements.getElement(CardElement);
      const { token, error } = await stripe.createToken(cardElement);

      if (error) {
        showSnackbar(error.message, "error");
        return;
      }

      const payload = {
        tokenId: token.id,
        userId: userData?._id,
        address: userAddress,
        order,
        tax,
        subtotal: renderSubTotal(selectedItems),
        taxAmount: renderPerCentageAmount(selectedItems),
        totalBill: finalAmount + Math.ceil(platformCharges),
        platformPercentage: parseInt(platformFee),
        shippingFee: shippingCost,
        paymentType: "Card",
        platformFee: Number(Math.ceil(platformCharges)),
        isPackageOrder: packageDetail ? true : false,
        packageDetail: {
          _id: packageDetail?._id || null,
          refferedBy,
          isApplied:
            refferedBy === null
              ? false
              : userData?.referralCode.trim() === refferedBy?.trim()
              ? false
              : true,
        },
      };

      console.log(payload, "PAYLOADDD");
      // return;
      setIsLoading(true);
      const response = await createEcomPaymentIntent(payload);

      if (response.data.status === "success") {
        navigate("/dashboard/e-commerce");
        showSnackbar(response.data.message, "success");

        if (!packageDetail) {
          dispatch(setCartData([]));
          localStorage.setItem("cart-data", JSON.stringify([]));
        }
        ReactGA.event({
          category: "Ecommerce",
          action: "Order Placed",
          value: payload.totalBill, // Total order amount
        });
      } else {
        showSnackbar(response.data.message, "error");
      }
    } catch (error) {
      showSnackbar(t("unexpectedError"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <AppLoader isVisible={isLoading} />
      <Box height={"100%"}>
        <Stack height={"100%"} alignItems={"space-between"}>
          {!packageDetail && (
            <Stack height={"100%"}>
              <Typography variant="h6" fontWeight={500}>
                {t("reviewCart")}
              </Typography>

              <Stack my={2} gap={2}>
                {cartData?.map((val, ind) => {
                  return <ReviewCartCard key={ind} cartData={val} />;
                })}
              </Stack>
            </Stack>
          )}

          {packageDetail && (
            <Stack height={"100%"}>
              <Box
                mb={1}
                px={1}
                display={"flex"}
                justifyContent={"center"}
                alignItems={"center"}
                borderRadius={1}
                maxWidth={"fit-content"}
                sx={{
                  background: "linear-gradient(45deg, #FF007F, #FF9E00)",
                }}
              >
                <Typography color={"text.white"}>{t("package")}</Typography>
              </Box>

              <Typography fontSize={"1.25rem"}>
                {packageDetail?.name[currentLanguage]}
              </Typography>

              <Typography variant="body1" fontWeight={400} lineHeight={1}>
                {t("productsIncludedInPackage")}
              </Typography>

              <Stack my={2} gap={2}>
                {packageDetail?.products?.map((val, ind) => {
                  return <ReviewCartCard key={ind} cartData={val} />;
                })}
              </Stack>
            </Stack>
          )}

          <Stack mt={2} height={"100%"}>
            <Divider sx={{ my: 1 }} />

            {renderSubTotal(selectedItems) < 300 && (
              <Typography
                variant="body2"
                fontStyle={"italic"}
                color="primary.main"
              >
                {t("noteFreeShipping", {
                  freeShippingCount: 300,
                })}
              </Typography>
            )}

            <TotalRow
              keyyy={t("subtotal")}
              value={`${CURRENCY} ${renderSubTotal(selectedItems)}`}
            />
                <TotalRow
              keyyy={t("platformCharges")}
              value={`${CURRENCY} ${Math.ceil(platformCharges)}`}
            />
            <TotalRow
              keyyy={t("shippingFee")}
              value={`${CURRENCY} ${shippingCost}`}
            />
            {/* <TotalRow keyyy={"Tax"} value={`${tax}%`} /> */}
            <TotalRow
              keyyy={t("taxAmount")}
              value={`${CURRENCY} ${renderPerCentageAmount(selectedItems)}`}
            />
            {/* <TotalRow keyyy={"Platform Percentage"} value={`${platformFee}%`} /> */}
        
            <TotalRow
              keyyy={`${t("totalCost")}`}
              value={`${CURRENCY} ${finalAmount + Math.ceil(platformCharges)}`}
              isLast={true}
            />
            {/* 
            <Typography variant="body2" color="text.slateGray">
              All prices include VAT
            </Typography> */}

            <Typography variant="body2" mb={0.5}>
              {t("enterCardDetail")}.
            </Typography>
            <Box
              sx={{
                border: "1px solid #ccc",
                borderRadius: "4px",
                padding: "10px",
              }}
            >
              <CardElement options={cardElementOptions} />
            </Box>
            <Stack direction={"row"} alignItems={"center"} gap={"5px"} my={0.5}>
              <Typography variant="body2" color={"#6c6f75"}>
                {t("securePaymentBy")}
              </Typography>
              <Typography fontSize={18} fontWeight={600} color={"primary.main"}>
                Stripe
              </Typography>
            </Stack>
            {/* <PaymentForm/> */}
            <GlobalButton
              variant={"contained"}
              btnText={"payNow"}
              // handleClickBtn={handlePlacedOrder}
              handleClickBtn={handOrderAndCreatePayment}
              styleProps={{
                mt: 4,
                width: "100%",
                height: 45,
              }}
            />
            {/* </Elements> */}
          </Stack>
        </Stack>
      </Box>
    </>
  );
};

export default Billing;
